import { Trans, useLingui } from '@lingui/react/macro';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import React, { useCallback, useState } from 'react';
import {
  FEATURE_FLAG_NAMES,
  FEATURE_FLAGS,
  FeatureFlagName,
  readOverride,
  resolveFeatureFlag,
  writeOverride,
} from 'shared/feature-flags';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';

interface FlagToggleProps {
  name: FeatureFlagName;
  userCreatedAt?: string;
}

/**
 * Переключатель «Включено / Выключено» без «По умолчанию». Пока пользователь ничего не выбрал,
 * подсвечено значение по дате регистрации (у кого аккаунт новее `since`, флаг уже включён);
 * выбор всегда пишется явным override, и дата регистрации для этого браузера больше не важна.
 */
const FlagToggle = ({ name, userCreatedAt }: FlagToggleProps) => {
  const { t } = useLingui();
  const definition = FEATURE_FLAGS[name];
  const [override, setOverride] = useState(() => readOverride(name));
  const [isChanged, setIsChanged] = useState(false);

  const isOn = resolveFeatureFlag({ override, userCreatedAt, since: definition.since });

  const change = useCallback(
    (next: boolean) => () => {
      const value = next ? 'on' : 'off';

      writeOverride(name, value);
      setOverride(value);
      setIsChanged(true);
    },
    [name],
  );

  return (
    <div className="border-lineSubtle bg-surfaceCard rounded-card flex flex-col gap-3 border p-4" data-testid={`feature-flag-${name}`}>
      <div className="flex flex-col gap-1">
        <UDText type="subhead" className="block">
          {t(definition.title)}
        </UDText>
        <UDText type="footnote" className="text-inkSecondary block">
          {t(definition.description)}
        </UDText>
      </div>

      <div className="flex gap-2">
        <UDButton variant={isOn ? 'primary' : 'secondary'} onClick={change(true)} aria-pressed={isOn} slim>
          <Trans>Включено</Trans>
        </UDButton>
        <UDButton variant={isOn ? 'secondary' : 'primary'} onClick={change(false)} aria-pressed={!isOn} slim>
          <Trans>Выключено</Trans>
        </UDButton>
      </div>

      {/* `useFeatureFlag` не пересчитывается от записи в localStorage — уже открытые экраны
          увидят выбор только после перезагрузки, поэтому говорим об этом прямо. */}
      {isChanged && (
        <UDText type="footnote" className="text-inkSecondary block">
          <Trans>Сохранено. Изменение применится после перезагрузки страницы.</Trans>
        </UDText>
      )}
    </div>
  );
};

/**
 * Вкладка настроек «Интерфейс»: включение новых версий экранов.
 * Выбор хранится в localStorage этого браузера (`imgame:ff:*`), то же пишет параметр `?flow=`.
 */
const Interface = () => {
  const { user } = useAuth();

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div className="flex flex-col gap-2">
        <UDText type="headline" className="block">
          <Trans>Интерфейс</Trans>
        </UDText>
        <UDText type="footnote" className="text-inkSecondary block">
          <Trans>Новые версии экранов можно включить или выключить. Настройка сохраняется в этом браузере.</Trans>
        </UDText>
      </div>

      <div className="flex flex-col gap-4">
        {FEATURE_FLAG_NAMES.map((name) => (
          <FlagToggle key={name} name={name} userCreatedAt={user?.created_at} />
        ))}
      </div>
    </div>
  );
};

export default Interface;
